import { useShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.props.context';
import {
  shiftModalRoutingService,
  useShiftModalUIContext,
} from 'modals/ShiftModal/ShiftModal.ui.context';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { useSendToAgenciesFormContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/SendToAgencies.form.context';
import { SendToAgenciesTargetType } from 'modals/ShiftModal/src/views/SendToAgenciesView/SendToAgenciesView.types';
import { useURLParameters } from 'hooks';
import { createContext, FC, useCallback, useContext, useMemo, useEffect } from 'react';

interface ISendToAgenciesProps {
  goToNextView: () => void;
}

export const SendToAgenciesPropsContext = createContext<ISendToAgenciesProps>(
  {} as ISendToAgenciesProps,
);

export const SendToAgenciesPropsProvider: FC = ({ children }) => {
  const { shiftModalActiveModalView } = useShiftModalUIContext();
  const { values } = useSendToAgenciesFormContext();
  const { data } = useShiftModalDataContext();
  const order = data?.order;
  const { goToSendToAgenciesView } = useShiftModalPropsContext();

  useEffect(() => {
    if (!values.send_to_agencies_target && shiftModalActiveModalView !== 'PreRequestOptionsView')
      goToSendToAgenciesView();
  }, [shiftModalActiveModalView]); // eslint-disable-line react-hooks/exhaustive-deps

  const [, { removeParam }] = useURLParameters();
  useEffect(() => {
    if (order && !order.canSendToAgency)
      removeParam(['shiftModalShiftId', 'shiftModalActiveModalView']);
  }, [data?.order, order?.canSendToAgency, removeParam]);

  const goToNextView = useCallback(() => {
    switch (shiftModalActiveModalView) {
      case 'PreRequestOptionsView':
        switch (values.send_to_agencies_target as SendToAgenciesTargetType) {
          case 'all':
            shiftModalRoutingService.goToView('SendToAgenciesSummaryView');
            break;
          case 'request_member':
            shiftModalRoutingService.goToView('RequestTeamMemberView');
            break;
          case 'request_agencies':
            shiftModalRoutingService.goToView('SelectAgenciesView');
            break;
        }
        break;

      case 'RequestTeamMemberView':
        if (order?.biddable) {
          shiftModalRoutingService.goToView('SendToAgenciesSummaryView');
        } else {
          shiftModalRoutingService.goToView('PostRequestOptionsView');
        }
        break;

      case 'PostRequestOptionsView':
      case 'SelectAgenciesView':
        shiftModalRoutingService.goToView('SendToAgenciesSummaryView');
        break;

      case 'SendToAgenciesSummaryView':
        break;
    }
  }, [order?.biddable, shiftModalActiveModalView, values.send_to_agencies_target]);

  const contextValue = useMemo(
    () => ({
      goToNextView,
    }),
    [goToNextView],
  );

  return (
    <SendToAgenciesPropsContext.Provider value={contextValue}>
      {children}
    </SendToAgenciesPropsContext.Provider>
  );
};

export const useSendToAgenciesPropsContext = (): ISendToAgenciesProps =>
  useContext(SendToAgenciesPropsContext);
